import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useRcsTemplatesList, useCreateRcsCampaign } from '@/hooks/useRcs';
import { useContactsList } from '@/hooks/useContacts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RcsCampaignFormDialog({ open, onOpenChange }: Props) {
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data: templatesData } = useRcsTemplatesList({ isActive: true });
  const templates = templatesData?.data ?? [];

  const { data: contactsData, isLoading: loadingContacts } = useContactsList({
    search: debouncedSearch || undefined,
    pageSize: 50,
  });
  const contacts = contactsData?.data ?? [];

  const { mutate: create, isPending } = useCreateRcsCampaign();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (open) {
      setName('');
      setTemplateId('');
      setSearch('');
      setSelectedContactIds([]);
      setError(null);
    }
  }, [open]);

  function toggleContact(id: string) {
    setSelectedContactIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function handleSubmit() {
    if (!name.trim()) {
      setError('Informe um nome para a campanha');
      return;
    }
    if (!templateId) {
      setError('Selecione um template');
      return;
    }
    if (selectedContactIds.length === 0) {
      setError('Selecione ao menos um contato');
      return;
    }
    setError(null);

    create(
      { name: name.trim(), templateId, contactIds: selectedContactIds },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nova campanha RCS</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rcs-campaign-name">Nome</Label>
            <Input
              id="rcs-campaign-name"
              placeholder="Promoção de julho"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Template RCS</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecionar template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Destinatários</Label>
            <Input
              placeholder="Buscar contatos…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-56 overflow-y-auto rounded border divide-y">
              {loadingContacts &&
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-2">
                    <Skeleton className="h-4 w-4 rounded" />
                    <Skeleton className="h-4 flex-1" />
                  </div>
                ))}
              {!loadingContacts && contacts.length === 0 && (
                <p className="py-4 text-center text-xs text-muted-foreground">
                  Nenhum contato encontrado
                </p>
              )}
              {contacts.map((c) => (
                <label
                  key={c.id}
                  className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-accent"
                >
                  <input
                    type="checkbox"
                    checked={selectedContactIds.includes(c.id)}
                    onChange={() => toggleContact(c.id)}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm">{c.name ?? c.phone}</span>
                  {c.name && (
                    <span className="ml-auto font-mono text-xs text-muted-foreground">
                      {c.phone}
                    </span>
                  )}
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedContactIds.length} selecionado(s)
            </p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" disabled={isPending} onClick={handleSubmit}>
            {isPending ? 'Criando...' : 'Criar campanha'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
